import { getPostAuthRedirect } from '@/lib/auth-navigation';
import type { OrganizationDataWithMembership, UserProfile } from '@/lib/types';
import type { NavigateOptions } from '@tanstack/react-router';

/**
 * Determines the navigation flow after authentication based on organization memberships
 */
export const determinePostAuthFlow = (organizations: OrganizationDataWithMembership[]) => {
  const pendingInvites = organizations.filter(org => 
    org.membership.status === 'pending'
  );
  const activeOrganizations = organizations.filter(org => 
    org.membership.status === 'active'
  );

  if (pendingInvites.length > 0) {
    return {
      shouldShowInvites: true,
      pendingInvites,
      activeOrganizations
    };
  }

  return {
    shouldShowInvites: false,
    pendingInvites: [],
    activeOrganizations
  };
};

type NavigateFn = (opts: NavigateOptions) => Promise<void>;

/**
 * Handles the normal flow navigation after all invitations are processed
 */
export const proceedWithNormalFlow = (
  organizations: OrganizationDataWithMembership[],
  user: UserProfile,
  navigate: NavigateFn,
  // The org accepted in THIS session (#6376): it outranks the profile's
  // last_organization_id, which was fetched before the accept and is stale.
  preferOrgId?: number | null
) => {
  const activeOrgs = organizations.filter(org =>
    org.membership.status === 'active'
  );

  if (activeOrgs.length === 0) {
    navigate({ to: '/welcome/organization-name' });
    return;
  }

  // Just-joined org, then last_organization_id, then first org
  let currentOrg = activeOrgs[0]; // fallback

  const preferredOrg = preferOrgId ? activeOrgs.find(org => org.id === preferOrgId) : undefined;
  if (preferredOrg) {
    currentOrg = preferredOrg;
  } else if (user?.last_organization_id) {
    const lastActiveOrg = activeOrgs.find(org => org.id === user.last_organization_id);
    if (lastActiveOrg) {
      currentOrg = lastActiveOrg;
    }
  }
  
  const redirectPath = getPostAuthRedirect(currentOrg);
  navigate({ to: redirectPath as any });
};